import "../compliance/compliance.css";
import "./uniform.css";
import { useEffect, useState } from "react";
import { DatePicker, Input, InputNumber, Modal, Select, Table, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { CaretDownFilled } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getUniformReceiptQuery } from "@src/util/query/uniform.query";
import { recordUniformReturnMutation, remindUniformReceiptMutation, signUniformReceiptInPersonMutation } from "@src/util/mutation/uniform.mutation";
import { DrawerShell, MODULE_STANDARD, RatingTag, SignaturePad, downloadCsv, ukDate, useUniformStandards } from "./uniformParts";

/**
 * The records BS 7499 / BS 10800 §7.6 asks for: the receipt an officer signs for uniform (and
 * the undertaking to return it), kit coming back, and where the company stands (Compliance).
 */

const useNotify = () => {
    const dispatch = useDispatch();
    return (message, error = false) => dispatch(setNotification({ error, notificationState: true, message }));
};

const ModalShell = ({ visible, close, title, children, footer, width = 680 }) => (
    <Modal open={visible} width={width} footer={footer} closable={false} onCancel={close} zIndex={1100} destroyOnClose>
        <div className="modal-header-title">
            <span style={{ letterSpacing: "0.2px" }}>{title}</span>
            <span onClick={close}><FontAwesomeIcon className="close" icon={faClose} /></span>
        </div>
        <div className="modal-body" style={{ margin: "16px 0 0" }}>{children}</div>
    </Modal>
);

const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
const signedText = (r) => (r?.status === "signed"
    ? `Signed ${dayjs(r.signedAt).format("DD/MM/YYYY HH:mm")} ${r.signedVia === "inPerson" ? `in person${r.witnessedByName ? `, witnessed by ${r.witnessedByName}` : ""}` : "in the app"}`
    : "Waiting for the officer's signature");

export const ReceiptStatusTag = ({ status }) => (status === "signed" ? <Tag color="green">Signed</Tag> : status === "awaiting" ? <Tag color="gold">Awaiting signature</Tag> : <span className="cmp-muted">--</span>);

/** The receipt as a printable page */
const printReceipt = (r) => {
    const rows = (r.lines || []).map((l) => `<tr><td>${escapeHtml(l.itemName)}</td><td>${escapeHtml(l.size || "One size")}</td><td>${l.quantity}</td><td>${escapeHtml(l.rating || "")}</td><td>${l.returnable ? "Return on leaving" : "Personal issue"}</td></tr>`).join("");
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Uniform receipt - ${escapeHtml(r.employeeName)}</title>
<style>body{font-family:Arial,sans-serif;color:#1b2734;margin:32px;font-size:13px}h1{font-size:20px;margin:0 0 4px}.muted{color:#5a6b7d}
table{width:100%;border-collapse:collapse;margin:16px 0}th,td{border:1px solid #cfd8e3;padding:6px 8px;text-align:left}th{background:#f2f5f8}
.decl{border:1px solid #cfd8e3;padding:12px;margin:16px 0;background:#fafbfc}.sig{height:90px;margin-top:8px}.foot{margin-top:24px;font-size:11px}</style></head><body>
<h1>Uniform and equipment receipt</h1><div class="muted">${escapeHtml(r.company)}</div>
<p><b>Officer:</b> ${escapeHtml(r.employeeName)}<br><b>Issued:</b> ${escapeHtml(ukDate(r.issuedDate))}${r.issuedByName ? ` by ${escapeHtml(r.issuedByName)}` : ""}<br><b>Receipt:</b> ${escapeHtml(r._id)}</p>
<table><thead><tr><th>Item</th><th>Size</th><th>Qty</th><th>Standard</th><th>On leaving</th></tr></thead><tbody>${rows}</tbody></table>
<div class="decl">${escapeHtml(r.declaration)}</div>
${r.signatureUrl ? `<div><b>Signature</b><br><img class="sig" src="${escapeHtml(r.signatureUrl)}"></div>` : ""}
<p>${escapeHtml(signedText(r))}</p>
<div class="foot muted">Record of uniform issued, signed for and undertaken to be returned - ${escapeHtml(MODULE_STANDARD)}.</div>
<script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`);
    win.document.close();
};

/**
 * One receipt: what was handed over, the words signed and the signature. Still waiting, a
 * manager can have the officer sign on this screen or send a reminder to the app.
 */
export const ReceiptModal = ({ receiptId, permission, onClose, onChanged }) => {
    const notify = useNotify();
    const canModify = permission?.access !== false && permission?.modify !== false;
    const { data, loading, refetch } = useQuery(getUniformReceiptQuery, { variables: { id: receiptId }, skip: !receiptId, fetchPolicy: "network-only" });
    const [signing, setSigning] = useState(false);
    const [signature, setSignature] = useState(null);
    const [sign, { loading: signBusy }] = useMutation(signUniformReceiptInPersonMutation);
    const [remind, { loading: remindBusy }] = useMutation(remindUniformReceiptMutation);
    useEffect(() => { setSigning(false); setSignature(null); }, [receiptId]);
    const r = data?.getUniformReceipt?.response;

    const doSign = async () => {
        try {
            await sign({ variables: { id: receiptId, signature } });
            notify("Receipt signed");
            setSigning(false);
            refetch();
            onChanged?.();
        } catch (err) { notify(err.message, true); }
    };
    const doRemind = async () => {
        try {
            await remind({ variables: { id: receiptId } });
            notify("Reminder sent to the officer's app");
        } catch (err) { notify(err.message, true); }
    };

    const footer = (
        <div className="drawer-footer" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {signing ? (
                <button className={!signature || signBusy ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={!signature || signBusy} onClick={doSign}>{signBusy ? <Spinner /> : "Save signature"}</button>
            ) : (
                r && <button className="drawer-filled-btn" onClick={() => printReceipt(r)}>Print</button>
            )}
            {r?.status === "awaiting" && canModify && !signing && <button className="drawer-outlined-btn" onClick={() => setSigning(true)}>Sign in person</button>}
            {r?.status === "awaiting" && canModify && !signing && <button className="drawer-outlined-btn" disabled={remindBusy} onClick={doRemind}>{remindBusy ? <Spinner /> : "Send reminder"}</button>}
            <button className="drawer-outlined-btn" onClick={signing ? () => setSigning(false) : onClose}>{signing ? "Back" : "Close"}</button>
        </div>
    );

    return (
        <ModalShell visible={!!receiptId} close={onClose} title="Uniform Receipt" footer={footer}>
            {loading || !r ? <Spinner /> : (
                <div className="uni-receipt">
                    <div className="uni-receipt-head">
                        <div>
                            <div className="cmp-person-name">{r.employeeName}</div>
                            <div className="cmp-muted">Issued {ukDate(r.issuedDate)}{r.issuedByName ? ` by ${r.issuedByName}` : ""} · {r.company}</div>
                        </div>
                        <ReceiptStatusTag status={r.status} />
                    </div>
                    <Table className="curvedTable uni-receipt-lines" size="small" pagination={false}
                        dataSource={(r.lines || []).map((l, i) => ({ ...l, key: i }))}
                        columns={[
                            { title: "Item", dataIndex: "itemName" },
                            { title: "Size", dataIndex: "size", render: (v) => v || <span className="cmp-muted">One size</span> },
                            { title: "Qty", dataIndex: "quantity", width: 60 },
                            { title: "Standard", dataIndex: "rating", render: (v) => <RatingTag rating={v} /> },
                            { title: "On leaving", dataIndex: "returnable", render: (v) => (v ? "Return" : <span className="cmp-muted">Personal issue</span>) },
                        ]} />
                    <div className="uni-declaration">{r.declaration}</div>
                    {signing ? (
                        <div className="uni-declaration-box">
                            <div className="cmp-hint" style={{ marginBottom: 6 }}>{r.employeeName} signs below to confirm the above.</div>
                            <SignaturePad onChange={setSignature} />
                        </div>
                    ) : (
                        <div className="uni-receipt-sign">
                            {r.signatureUrl && <img className="uni-receipt-signature" src={r.signatureUrl} alt="Signature" />}
                            <span className={r.status === "signed" ? "" : "cmp-muted"}>{signedText(r)}</span>
                        </div>
                    )}
                    <div className="cmp-hint" style={{ marginTop: 10 }}>{MODULE_STANDARD}: officers sign for uniform received and undertake to return it on leaving.</div>
                </div>
            )}
        </ModalShell>
    );
};

/** Receipts for one employee (or several), newest first */
export const UniformReceiptsTable = ({ receipts, loading, showEmployee = true, onOpen, emptyText }) => (
    <Table
        className="curvedTable"
        loading={loading}
        pagination={false}
        dataSource={(receipts || []).map((r) => ({ ...r, key: r._id }))}
        locale={{ emptyText: emptyText || "No receipts yet" }}
        columns={[
            { title: "Issued", dataIndex: "issuedDate", width: 120, render: (v) => ukDate(v) },
            showEmployee && { title: "Employee", dataIndex: "employeeName", render: (v, row) => <Link className="cmp-link" to={`/user/employee-detail/${row.employeeId}/tab/uniform`}>{v || "--"}</Link> },
            { title: "Items", dataIndex: "summary" },
            { title: "Status", dataIndex: "status", render: (v, row) => (
                <div className="cmp-person">
                    <span><ReceiptStatusTag status={v} /></span>
                    <span className="cmp-muted">{v === "signed" ? `${ukDate(row.signedAt)} · ${row.signedVia === "inPerson" ? "in person" : "in the app"}` : row.issuedByName ? `Issued by ${row.issuedByName}` : ""}</span>
                </div>
            ) },
            { title: "", dataIndex: "open", align: "right", render: (_, row) => <button className="sm-btn" onClick={() => onOpen(row._id)}>{row.status === "awaiting" ? "Open / sign" : "View"}</button> },
        ].filter(Boolean)}
    />
);

// ---------------------------------------------------------------- returns

/**
 * Kit coming back - on leaving, or swapped for new - for the rows ticked on the Issued tab:
 * how many of each, and whether it came back fit to reissue, damaged, or not at all.
 */
export const ReturnDrawer = ({ rows, employee, onClose, onSaved }) => {
    const notify = useNotify();
    const visible = !!rows?.length;
    const { returnOutcomes } = useUniformStandards(!visible);
    const [picks, setPicks] = useState({});
    const [date, setDate] = useState(dayjs());
    const [note, setNote] = useState("");
    const [record, { loading: busy }] = useMutation(recordUniformReturnMutation);
    useEffect(() => {
        if (!visible) return;
        setPicks(Object.fromEntries(rows.map((row) => [row._id, { quantity: row.held, outcome: "returned" }])));
        setDate(dayjs());
        setNote("");
    }, [visible, rows?.map((r) => r._id).join()]);

    const pick = (row, patch) => setPicks((prev) => ({ ...prev, [row._id]: { ...prev[row._id], ...patch } }));
    const submit = async () => {
        try {
            await record({ variables: { input: {
                employeeId: employee._id, returnedDate: date.format("YYYY-MM-DD"), note: note.trim() || null,
                lines: rows.map((row) => ({ issueId: row._id, quantity: picks[row._id]?.quantity || row.held, outcome: picks[row._id]?.outcome || "returned" })),
            } } });
            notify("Return recorded");
            onSaved?.();
            onClose();
        } catch (err) { notify(err.message, true); }
    };

    return (
        <DrawerShell visible={visible} close={onClose} title="Record Return"
            footer={
                <div className="drawer-footer" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button className={busy ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={busy} onClick={submit}>{busy ? <Spinner /> : "Record return"}</button>
                    <button className="drawer-outlined-btn" disabled={busy} onClick={onClose}>Cancel</button>
                </div>
            }>
            <div className="uni-form">
                <section className="uni-form-section">
                    <div className="uni-form-title">What's coming back{employee?.name ? ` from ${employee.name}` : ""}</div>
                    <div className="uni-form-note">Company property is returned when employment ends. Anything not recovered stays on the record.</div>
                    {(rows || []).map((row) => (
                        <div key={row._id} className="uni-option uni-return-row">
                            <div className="uni-return-item">
                                <span className="uni-option-title">{row.itemName}{row.size ? ` (${row.size})` : ""}</span>
                                <span className="uni-option-text">Issued {ukDate(row.issuedDate)} · {row.held} held · {row.returnable ? "company property" : "personal issue"}</span>
                            </div>
                            <div className="uni-return-fields">
                                <InputNumber min={1} max={row.held} precision={0} style={{ width: 80 }} value={picks[row._id]?.quantity ?? row.held} onChange={(v) => pick(row, { quantity: v || 1 })} />
                                <Select className="custom-select" suffixIcon={<CaretDownFilled />} style={{ width: 190 }}
                                    value={picks[row._id]?.outcome || "returned"} onChange={(v) => pick(row, { outcome: v })} options={returnOutcomes} />
                            </div>
                        </div>
                    ))}
                </section>
                <section className="uni-form-section">
                    <div className="uni-form-title">When</div>
                    <div className="uni-form-row">
                        <div>
                            <label className="uni-form-label">Date returned</label>
                            <DatePicker className="generic-input-control" style={{ width: "100%" }} format="DD/MM/YYYY" value={date} allowClear={false}
                                disabledDate={(d) => d && d.isAfter(dayjs(), "day")} onChange={(v) => v && setDate(v)} />
                        </div>
                        <div>
                            <label className="uni-form-label">Note <span className="cmp-muted">(optional)</span></label>
                            <Input className="generic-input-control" value={note} maxLength={500} placeholder="e.g. Leaver kit check" onChange={(e) => setNote(e.target.value)} />
                        </div>
                    </div>
                </section>
            </div>
        </DrawerShell>
    );
};

// ---------------------------------------------------------------- compliance

/** One employee's standing, above their Uniform tab: only what needs doing */
export const EmployeeComplianceStrip = ({ compliance, isLeaver }) => {
    if (!compliance) return null;
    // supplier staff and people not working aren't checked for kit (the server says why)
    const skipped = compliance.notChecked;
    const missing = compliance.missingRequired?.[0]?.missing || [];
    const noHiVis = compliance.noHiVis?.length > 0 && compliance.setup?.hasHiVis;
    const renewals = compliance.renewals || [];
    const awaiting = compliance.counts?.awaitingSignature || 0;
    const holding = compliance.leavers?.[0];
    const notes = [
        skipped?.reason === "supplier" && { tone: "info", text: `Supplier staff${skipped.supplier ? ` (${skipped.supplier})` : ""} - their supplier provides their uniform, so they aren't checked against your standard issue.` },
        skipped?.reason === "notWorking" && !isLeaver && { tone: "info", text: `${skipped.status || "Not working"} - not checked for kit while they aren't working.` },
        missing.length > 0 && { tone: "warn", text: `Standard issue not held: ${missing.join(", ")}` },
        noHiVis && { tone: "warn", text: "No hi-vis held (BS 7499: hi-vis available to every officer)" },
        renewals.some((r) => r.overdue) && { tone: "warn", text: `Overdue for renewal: ${renewals.filter((r) => r.overdue).map((r) => r.itemName).join(", ")}` },
        renewals.some((r) => !r.overdue) && { tone: "info", text: `Due for renewal within 30 days: ${renewals.filter((r) => !r.overdue).map((r) => `${r.itemName} (${ukDate(r.renewDue)})`).join(", ")}` },
        awaiting > 0 && { tone: "info", text: `${awaiting} receipt${awaiting === 1 ? "" : "s"} waiting for their signature` },
        holding && { tone: "warn", text: `Left the company and still holds ${holding.held} item${holding.held === 1 ? "" : "s"} of company property - record the return` },
    ].filter(Boolean);
    if (!notes.length) {
        // nothing to check against yet - don't call that "up to standard"
        const nothingSetUp = !compliance.setup?.requiredItems?.length && !compliance.setup?.hasHiVis;
        if (nothingSetUp && !isLeaver) return <div className="uni-compliance-strip"><div className="uni-compliance-note uni-compliance-info">No standard issue set up yet - mark the items every officer needs as Required in Settings &gt; Uniform.</div></div>;
        return <div className="uni-compliance-ok">{isLeaver ? "All company property accounted for." : "Up to standard: standard issue and hi-vis held, nothing overdue, every receipt signed."}</div>;
    }
    return (
        <div className="uni-compliance-strip">
            {notes.map((n, i) => <div key={i} className={`uni-compliance-note uni-compliance-${n.tone}`}>{n.text}</div>)}
        </div>
    );
};

export const COMPLIANCE_VIEWS = [
    { key: "missingRequired", label: "Standard issue not held", count: "missingRequired" },
    { key: "noHiVis", label: "No hi-vis held", count: "noHiVis" },
    { key: "renewals", label: "Renewal due or overdue", count: ["renewalDue", "renewalOverdue"] },
    { key: "awaiting", label: "Receipts awaiting signature", count: "awaitingSignature" },
    { key: "leavers", label: "Leavers holding company kit", count: "leaversHolding" },
];
export const complianceCount = (counts, view) => (Array.isArray(view.count) ? view.count.reduce((s, k) => s + (counts?.[k] || 0), 0) : counts?.[view.count] || 0);
const EmployeeLink = ({ row }) => <Link className="cmp-link" to={`/user/employee-detail/${row.employeeId}/tab/uniform`}>{row.employeeName || "--"}</Link>;

/**
 * Where the company stands against BS 7499 / BS 10800 §7.6 - one check at a time (chosen in the
 * pop-up's toolbar, counts in its stat tiles), each a list of the people to sort out (their name
 * opens their Uniform tab).
 */
export const CompliancePanel = ({ compliance, loading, view, onOpenReceipt, search = "" }) => {
    const counts = compliance?.counts;
    const setup = compliance?.setup;
    const columns = {
        missingRequired: [
            { title: "Employee", dataIndex: "employeeName", render: (_, row) => <EmployeeLink row={row} /> },
            { title: "Standard issue not held", dataIndex: "missing", render: (v) => v.join(", ") },
        ],
        noHiVis: [{ title: "Employee", dataIndex: "employeeName", render: (_, row) => <EmployeeLink row={row} /> }],
        renewals: [
            { title: "Employee", dataIndex: "employeeName", render: (_, row) => <EmployeeLink row={row} /> },
            { title: "Item", dataIndex: "itemName", render: (v, row) => `${v}${row.size ? ` (${row.size})` : ""}` },
            { title: "Issued", dataIndex: "issuedDate", render: (v) => ukDate(v) },
            { title: "Renewal due", dataIndex: "renewDue", render: (v, row) => (row.overdue ? <Tag color="red">Overdue {ukDate(v)}</Tag> : <Tag color="gold">{ukDate(v)}</Tag>) },
        ],
        awaiting: [
            { title: "Issued", dataIndex: "issuedDate", render: (v) => ukDate(v) },
            { title: "Employee", dataIndex: "employeeName", render: (_, row) => <EmployeeLink row={row} /> },
            { title: "Items", dataIndex: "summary" },
            { title: "", dataIndex: "open", align: "right", render: (_, row) => <button className="sm-btn" onClick={() => onOpenReceipt(row._id)}>Open / sign</button> },
        ],
        leavers: [
            { title: "Employee", dataIndex: "employeeName", render: (_, row) => <EmployeeLink row={row} /> },
            { title: "Status", dataIndex: "status" },
            { title: "Company property still held", dataIndex: "lines", render: (lines) => lines.map((l) => `${l.held} x ${l.itemName}${l.size ? ` (${l.size})` : ""}`).join(", ") },
        ],
    }[view];
    // search: the employee's name, or an item on the row
    const needle = search.trim().toLowerCase();
    const rows = (compliance?.[view] || []).filter((row) => !needle || [row.employeeName, row.itemName, row.summary, ...(row.missing || []), ...(row.lines || []).map((l) => l.itemName)]
        .filter(Boolean).join(" ").toLowerCase().includes(needle));

    return (
        <div className="uni-compliance">
            {setup && (!setup.hasHiVis || !setup.requiredItems.length) && (
                <div className="uni-compliance-strip" style={{ marginBottom: 10 }}>
                    {!setup.hasHiVis && <div className="uni-compliance-note uni-compliance-warn">No hi-vis item is set up. BS 7499 expects hi-vis to be available to every officer - add one in Settings &gt; Uniform.</div>}
                    {!setup.requiredItems.length && <div className="uni-compliance-note uni-compliance-info">No item is marked standard issue, so nothing is checked for every officer. Mark them in Settings &gt; Uniform.</div>}
                </div>
            )}
            <div className="cmp-hint" style={{ marginBottom: 8 }}>
                {counts ? `${counts.officers} officer${counts.officers === 1 ? "" : "s"} checked - your own staff who are working (supplier staff, leavers and inactive employees aren't).` : ""} {MODULE_STANDARD}.
            </div>
            <Table className="curvedTable" loading={loading} pagination={{ pageSize: 25, hideOnSinglePage: true }} columns={columns}
                dataSource={rows.map((row, i) => ({ ...row, key: row._id || `${row.employeeId}-${row.itemId || ""}-${i}` }))}
                locale={{ emptyText: loading ? " " : needle ? "No one matches" : "Nothing to sort out here" }} />
        </div>
    );
};

/** The current Compliance list as CSV */
export const exportCompliance = (compliance, view) => {
    const rows = compliance?.[view] || [];
    const file = `uniform-compliance-${view}-${dayjs().format("YYYY-MM-DD")}.csv`;
    if (view === "missingRequired") return downloadCsv(file, ["Employee", "Standard issue not held"], rows.map((r) => [r.employeeName, r.missing.join("; ")]));
    if (view === "noHiVis") return downloadCsv(file, ["Employee"], rows.map((r) => [r.employeeName]));
    if (view === "renewals") return downloadCsv(file, ["Employee", "Item", "Size", "Issued", "Renewal due", "Overdue"], rows.map((r) => [r.employeeName, r.itemName, r.size || "One size", ukDate(r.issuedDate), ukDate(r.renewDue), r.overdue ? "Yes" : "No"]));
    if (view === "awaiting") return downloadCsv(file, ["Issued", "Employee", "Items", "Issued by"], rows.map((r) => [ukDate(r.issuedDate), r.employeeName, r.summary, r.issuedByName]));
    return downloadCsv(file, ["Employee", "Status", "Company property still held"], rows.map((r) => [r.employeeName, r.status, r.lines.map((l) => `${l.held} x ${l.itemName}${l.size ? ` (${l.size})` : ""}`).join("; ")]));
};
